import React, { MutableRefObject, useRef, useState, useEffect } from 'react'

import {
  Icon,
  ContextMenuLabel,
  DropdownWrapper,
  ContextMenuNav,
} from './ContextMenu.styles'

import { useDropdownState } from '@sketch/components'
import { LayerTree, buildRoot } from './layerTree'

interface ContextMenuListProps {
  trees: LayerTree[]
  level: number
  selectedLayerId?: string
  onHover?: (hoverId: string) => void
  onSelect?: (layerId: string) => void
  className?: string
  ref?: MutableRefObject<HTMLOListElement | null>
}

// A recursive component that renders a layer tree into ordered lists
const ContextMenuList = ({
  className,
  trees,
  selectedLayerId = 'null', // root is selected by default
  onHover,
  onSelect,
  level,
  ref,
}: ContextMenuListProps) => {
  const handleEventWith = (id: string, f?: (id: string) => void) => (e: {
    stopPropagation: () => void
  }) => {
    if (f) {
      e.stopPropagation()
      // ^ recursive react element needs to prevent event from being
      // fired multiple times in the elements higher on the call stack
      f(id)
    }
  }

  return (
    <ol className={className} key={`level${level}`} ref={ref}>
      {trees.map(t => {
        return (
          <li
            key={`level${level}-${t.name}-${t.id}`}
            onClick={handleEventWith(t.id, onSelect)}
          >
            <ContextMenuLabel
              level={level}
              isSelected={t.id === selectedLayerId}
            >
              <Icon layerTree={t} />
              <p>{t.name}</p>
            </ContextMenuLabel>
            <ContextMenuList
              trees={t.children}
              level={level + 1}
              onHover={onHover}
              onSelect={onSelect}
              selectedLayerId={selectedLayerId}
            />
          </li>
        )
      })}
    </ol>
  )
}

interface ContextMenuProps {
  root: { name: string } | undefined
  trees: LayerTree[]
  selectedLayerId?: string
  onHover?: (layerId: string) => void
  onSelect?: (layerId: string) => void
  className?: string
  visible?: boolean
  // menu position (relative to the artboard element)
  contextMenuX: number
  contextMenuY: number
  staticMenu?: boolean
}

// TODO: Review react/display-name ESLint issues
//  see: https://github.com/sketch-hq/Cloud/issues/14818
// eslint-disable-next-line react/display-name
const ContextMenu: React.FC<ContextMenuProps> = React.memo(
  ({
    root,
    trees,
    selectedLayerId,
    onHover,
    onSelect,
    className,
    visible = false,
    contextMenuX,
    contextMenuY,
    staticMenu = false,
    ...rest
  }) => {
    // to update the poppers.js with a new layout it is necessary to
    // clear the vdom (with null) and re-render it fully again
    const [isRerendering, rerender] = useState(false)

    // a poppers layout update is needed whenever the position changes (which
    // means a new menu with new contents and new layout)
    const [{ posX, posY }, setPos] = useState({
      posX: contextMenuX,
      posY: contextMenuY,
    })

    // menu is being done with our stateless Dropdown component to maintain
    // consistency with other menus implementations in the cloud
    const dropdownContentRef = useRef<HTMLOListElement>(null)
    const dropdown = useDropdownState({
      onToggle: () => {},
    })

    useEffect(() => {
      // to update the context menu contents and layout when a new position
      // is set, we need to do two passes, where the first clears everything
      // an alternative would be to expose the react-popper `scheduleUpdate()`
      // in our Popper, Popup and Dropdown
      if (contextMenuX !== posX || contextMenuY !== posY) {
        rerender(true)
        setPos({ posX: contextMenuX, posY: contextMenuY })
      } else {
        rerender(false)
      }
    }, [contextMenuX, contextMenuY, isRerendering, posX, posY])

    if (trees.length === 0 || (isRerendering && !staticMenu)) {
      return null
    }

    return (
      <DropdownWrapper
        {...dropdown}
        onClickOutside={dropdown.toggleDropdown}
        placement="bottom-start"
        style={{
          left: contextMenuX,
          top: contextMenuY,
        }}
        contentStyle={{ maxWidth: 500 }}
        className={className}
        visible={visible} // through the visible flag
        maxWidth="100%"
        {...rest}
      >
        <ContextMenuNav ref={dropdownContentRef}>
          <ContextMenuList
            // hide an undefined artboard
            trees={root ? buildRoot(root.name, trees) : trees}
            level={0}
            onHover={onHover}
            onSelect={onSelect}
            selectedLayerId={selectedLayerId}
          />
        </ContextMenuNav>
      </DropdownWrapper>
    )
  }
)

export default ContextMenu
